// src/features/stores/storeThunks.js - UPDATED VERSION
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * GET /api/stores/
 * Everyone sees all stores (but different fields if not admin).
 */
export const fetchStoresThunk = createAsyncThunk(
  'stores/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/stores/');
      return response.data;
    } catch (err) {
      return rejectWithValue('Failed to fetch stores');
    }
  }
);

/**
 * GET /api/stores/:id/
 */
export const fetchStoreThunk = createAsyncThunk(
  'stores/fetchOne',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/stores/${storeId}/`);
      return response.data;
    } catch (err) {
      return rejectWithValue('Failed to fetch store');
    }
  }
);

/**
 * Create a new store (Admin only)
 * Now handles incentive rates through set_incentive_rates field
 */
export const createStoreThunk = createAsyncThunk(
  'stores/create',
  async (storeData, { rejectWithValue }) => {
    try {
      const isFormData = storeData instanceof FormData;
      
      const config = {
        headers: {}
      };
      
      if (isFormData) {
        config.headers['Content-Type'] = 'multipart/form-data';
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
      
      const response = await axiosInstance.post('/api/stores/', storeData, config);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can create stores');
      }
      
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        
        if (typeof errorData === 'object' && errorData !== null) {
          const errorMessages = [];
          
          Object.entries(errorData).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              errorMessages.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`);
            }
          });
          
          if (errorMessages.length > 0) {
            return rejectWithValue(errorMessages.join('; '));
          }
        }
        
        return rejectWithValue(errorData?.detail || 'Invalid data provided');
      }
      
      if (err.response?.status === 413) {
        return rejectWithValue('File size too large. Please choose smaller images.');
      }
      
      if (err.response?.status === 415) {
        return rejectWithValue('Unsupported file type. Please use JPG, PNG, or GIF images.');
      }
      
      return rejectWithValue(err.response?.data?.detail || 'Failed to create store');
    }
  }
);

/**
 * Update store (Admin only)
 * Now handles incentive rates through set_incentive_rates field
 */
export const updateStoreThunk = createAsyncThunk(
  'stores/update',
  async ({ storeId, updates }, { rejectWithValue }) => {
    try {
      const isFormData = updates instanceof FormData;
      
      const config = {
        headers: {}
      };
      
      if (isFormData) {
        config.headers['Content-Type'] = 'multipart/form-data';
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
      
      const response = await axiosInstance.patch(`/api/stores/${storeId}/`, updates, config);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can update stores');
      }
      
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        
        if (typeof errorData === 'object' && errorData !== null) {
          const errorMessages = [];
          
          Object.entries(errorData).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              errorMessages.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`);
            }
          });
          
          if (errorMessages.length > 0) {
            return rejectWithValue(errorMessages.join('; '));
          }
        }
        
        return rejectWithValue(errorData?.detail || 'Invalid update data provided');
      }
      
      if (err.response?.status === 413) {
        return rejectWithValue('File size too large. Please choose smaller images.');
      }
      
      if (err.response?.status === 415) {
        return rejectWithValue('Unsupported file type. Please use JPG, PNG, or GIF images.');
      }
      
      return rejectWithValue('Failed to update store');
    }
  }
);

// ============================================================================
// NEW: INCENTIVE RATE MANAGEMENT THUNKS
// ============================================================================

/**
 * Fetch incentive rates for a specific store
 * GET /api/store-incentive-rates/by-store/:storeId/
 */
export const fetchStoreIncentiveRatesThunk = createAsyncThunk(
  'stores/fetchIncentiveRates',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/store-incentive-rates/by-store/${storeId}/`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch incentive rates');
    }
  }
);

/**
 * Update incentive rates for a store (bulk update)
 * POST /api/store-incentive-rates/bulk-update/
 */
export const updateStoreIncentiveRatesThunk = createAsyncThunk(
  'stores/updateIncentiveRates',
  async ({ storeId, rates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/store-incentive-rates/bulk-update/`,
        {
          store_id: storeId,
          rates: rates
        }
      );
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can update incentive rates');
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to update incentive rates');
    }
  }
);

/**
 * Update incentive rates directly through store endpoint
 * PATCH /api/stores/:id/update_incentive_rates/
 */
export const updateStoreIncentiveRatesDirectThunk = createAsyncThunk(
  'stores/updateIncentiveRatesDirect',
  async ({ storeId, rates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/stores/${storeId}/update_incentive_rates/`,
        { rates }
      );
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can update incentive rates');
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to update incentive rates');
    }
  }
);

// ============================================================================
// INCENTIVE STATS THUNKS (UPDATED)
// ============================================================================

/**
 * Fetch incentive statistics for a store with breakdown by incident type
 * GET /api/stores/:id/incentive_stats/
 */
export const fetchStoreIncentiveStatsThunk = createAsyncThunk(
  'stores/fetchIncentiveStats',
  async ({ storeId, year, month }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      
      const response = await axiosInstance.get(
        `/api/stores/${storeId}/incentive_stats/?${params}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch store incentive stats');
    }
  }
);

/**
 * DEPRECATED: Use updateStoreIncentiveRatesThunk instead
 * Update store incentive amount (admin only)
 * PATCH /api/stores/:id/
 */
export const updateStoreIncentiveAmountThunk = createAsyncThunk(
  'stores/updateIncentiveAmount',
  async ({ storeId, incentiveAmount }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/stores/${storeId}/`, {
        incentive_amount: incentiveAmount
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update store incentive amount');
    }
  }
);

/**
 * Fetch stores with their incentive information (admin view)
 * GET /api/stores/
 */
export const fetchStoresWithIncentiveInfoThunk = createAsyncThunk(
  'stores/fetchWithIncentiveInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/stores/');
      return response.data;
    } catch (err) {
      return rejectWithValue('Failed to fetch stores with incentive info');
    }
  }
);

/**
 * Get store incentive performance comparison with breakdown by type
 */
export const fetchStoreIncentiveComparisonThunk = createAsyncThunk(
  'stores/fetchIncentiveComparison',
  async ({ year, month, storeIds }, { rejectWithValue }) => {
    try {
      const promises = storeIds.map(storeId =>
        axiosInstance.get(`/api/stores/${storeId}/incentive_stats/`, {
          params: { year, month }
        }).then(response => ({
          storeId,
          ...response.data
        }))
      );
      
      const results = await Promise.all(promises);
      
      const sortedResults = results.sort((a, b) => 
        parseFloat(b.total_amount) - parseFloat(a.total_amount)
      );
      
      return {
        year,
        month,
        comparison: sortedResults
      };
    } catch (err) {
      return rejectWithValue('Failed to fetch store incentive comparison');
    }
  }
);

/**
 * Get store monthly incentive trends with breakdown by type
 */
export const fetchStoreIncentiveTrendsThunk = createAsyncThunk(
  'stores/fetchIncentiveTrends',
  async ({ storeId, year, monthsCount = 6 }, { rejectWithValue }) => {
    try {
      const currentMonth = new Date().getMonth() + 1;
      const months = [];
      
      for (let i = monthsCount - 1; i >= 0; i--) {
        let month = currentMonth - i;
        let yearToUse = year;
        
        if (month <= 0) {
          month += 12;
          yearToUse -= 1;
        }
        
        months.push({ month, year: yearToUse });
      }
      
      const promises = months.map(({ month, year: monthYear }) =>
        axiosInstance.get(`/api/stores/${storeId}/incentive_stats/`, {
          params: { year: monthYear, month }
        }).then(response => ({
          month,
          year: monthYear,
          monthName: new Date(monthYear, month - 1).toLocaleString('default', { month: 'long' }),
          ...response.data
        }))
      );
      
      const results = await Promise.all(promises);
      
      return {
        storeId,
        trends: results
      };
    } catch (err) {
      return rejectWithValue('Failed to fetch store incentive trends');
    }
  }
);

// ============================================================================
// STORE REPORTS THUNK
// ============================================================================

/**
 * Fetch store reports summary data
 * GET /api/stores/reports-summary/
 */
export const fetchStoreReportsThunk = createAsyncThunk(
  'stores/fetchReportsSummary',
  async ({ startDate, endDate, search } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (search) params.append('search', search);
      
      const response = await axiosInstance.get(`/api/stores/reports-summary/?${params}`);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Access denied. Only employees can view store reports summary.');
      }
      
      if (err.response?.status === 400) {
        return rejectWithValue(err.response?.data?.detail || 'Invalid request parameters.');
      }
      
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch store reports summary.');
    }
  }
);

// ============================================================================
// STORE NOTES THUNKS
// ============================================================================

/**
 * Fetch all stores with their notes
 * GET /api/store-notes/all-stores-with-notes/
 */
export const fetchStoresWithNotesThunk = createAsyncThunk(
  'stores/fetchWithNotes',
  async ({ search, importantOnly } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (importantOnly) params.append('important_only', 'true');
      
      const response = await axiosInstance.get(
        `/api/store-notes/all-stores-with-notes/?${params}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch stores with notes');
    }
  }
);

/**
 * Fetch notes for a specific store
 * GET /api/stores/:id/notes/
 */
export const fetchStoreNotesThunk = createAsyncThunk(
  'stores/fetchNotes',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/stores/${storeId}/notes/`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch store notes');
    }
  }
);

/**
 * Fetch notes for multiple stores
 * GET /api/store-notes/by-stores/?store_ids=1,2,3
 */
export const fetchMultipleStoreNotesThunk = createAsyncThunk(
  'stores/fetchMultipleNotes',
  async (storeIds, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/store-notes/by-stores/?store_ids=${storeIds.join(',')}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch notes for stores');
    }
  }
);

/**
 * Create a new store note (admin only)
 * POST /api/store-notes/
 */
export const createStoreNoteThunk = createAsyncThunk(
  'stores/createNote',
  async (noteData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/store-notes/', noteData);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can create store notes');
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to create note');
    }
  }
);

/**
 * Update a store note (admin only)
 * PATCH /api/store-notes/:id/
 */
export const updateStoreNoteThunk = createAsyncThunk(
  'stores/updateNote',
  async ({ noteId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/store-notes/${noteId}/`, updates);
      return response.data;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can update store notes');
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to update note');
    }
  }
);

/**
 * Delete a store note (admin only)
 * DELETE /api/store-notes/:id/
 */
export const deleteStoreNoteThunk = createAsyncThunk(
  'stores/deleteNote',
  async (noteId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/store-notes/${noteId}/`);
      return noteId;
    } catch (err) {
      if (err.response?.status === 403) {
        return rejectWithValue('Only Admin can delete store notes');
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete note');
    }
  }
);